import { suite, tools } from "@perprotocol/catalog";
import { PerProtocolLink } from "@perprotocol/ui";
import { productUrlForHost, type SiteRoute } from "./routing";

const products = [
  {
    ...tools.fieldguide,
    icon: (
      <svg viewBox="0 0 64 64" aria-hidden="true">
        <circle cx="32" cy="32" r="21" />
        <path d="m39 25-4 10-10 4 4-10 10-4Z" />
        <path className="icon-accent" d="M32 7v5M32 52v5M7 32h5M52 32h5" />
      </svg>
    ),
  },
  {
    ...tools.ctcae,
    icon: (
      <svg viewBox="0 0 64 64" aria-hidden="true">
        <path d="M20 13h24a5 5 0 0 1 5 5v33H15V18a5 5 0 0 1 5-5Z" />
        <path d="M24 13v-2h16v2M23 25h18M23 33h18M23 41h11" />
        <path className="icon-accent" d="m38 41 3 3 7-8" />
      </svg>
    ),
  },
  {
    ...tools.weightdelta,
    icon: (
      <svg viewBox="0 0 64 64" aria-hidden="true">
        <path d="M16 18h32v34H16zM22 18v-6h20v6M24 28h16M32 28v12" />
        <path className="icon-accent" d="m27 35 5 5 5-5" />
      </svg>
    ),
  },
  {
    ...tools.platinuminterval,
    icon: (
      <svg viewBox="0 0 64 64" aria-hidden="true">
        <path d="M18 14h28a5 5 0 0 1 5 5v31H13V19a5 5 0 0 1 5-5Z" />
        <path d="M22 10v9M42 10v9M13 26h38M21 35h8M35 35h8M21 43h8" />
        <path className="icon-accent" d="M37 43h8M41 39v8" />
      </svg>
    ),
  },
] as const;

const metadata = {
  home: {
    title: "Per Protocol · Clinical research tools",
    description: "Clinical research tools for the boots on the ground.",
    path: "/",
  },
  about: {
    title: "About · Per Protocol",
    description: "Practical, source-linked tools for the people doing the day-to-day work of clinical research.",
    path: "/about",
  },
} as const;

function setMeta(name: string, content: string, property = false) {
  const selector = property ? `meta[property="${name}"]` : `meta[name="${name}"]`;
  let element = document.head.querySelector<HTMLMetaElement>(selector);
  if (!element) {
    element = document.createElement("meta");
    element.setAttribute(property ? "property" : "name", name);
    document.head.append(element);
  }
  element.content = content;
}

export function applySiteMetadata(route: SiteRoute) {
  const page = metadata[route];
  const url = `https://perprotocol.org${page.path}`;
  document.title = page.title;
  setMeta("description", page.description);
  setMeta("og:title", page.title, true);
  setMeta("og:description", page.description, true);
  setMeta("og:url", url, true);

  const canonical = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (canonical) canonical.href = url;
}

function Header({ about = false }: { about?: boolean }) {
  return (
    <header className="site-header">
      <PerProtocolLink className="wordmark" href="/" aria-label={`${suite.name} home`} />
      {about
        ? <a className="corner-link" href="/">Home</a>
        : <a className="corner-link" href="/about">About</a>}
    </header>
  );
}

function LandingPage() {
  return (
    <div className="site-page">
      <Header />
      <main className="landing">
        <section className="welcome">
          <h1>Welcome to<br />Per Protocol</h1>
          <p>Clinical research tools for the boots on the ground</p>
        </section>

        <nav className="products" aria-label="Per Protocol tools">
          {products.map((product) => (
            <a
              className="product"
              href={productUrlForHost(product.url, window.location.hostname)}
              key={product.name}
            >
              <span className="product-icon">{product.icon}</span>
              <span className="product-copy">
                <strong>{product.name}</strong>
                <small>{product.description}</small>
              </span>
              <span className="product-arrow" aria-hidden="true">↗</span>
            </a>
          ))}
        </nav>
      </main>
    </div>
  );
}

function Team() {
  return (
    <section className="team" id="team" aria-labelledby="team-heading">
      <h2 id="team-heading">Team</h2>
      <div className="team-list">
        <article className="team-member">
          <img
            src="/team/maria-kim-heinert.png"
            width="1122"
            height="1402"
            loading="lazy"
            decoding="async"
            alt="Maria Kim-Heinert"
          />
          <div className="team-copy">
            <h3>Maria Kim-Heinert</h3>
            <span>Application Lead</span>
            <p>
              Software engineer turned clinical research coordinator, Maria bridges the gap
              between the people doing clinical research and the technology needed to support
              them. She leads the design and development of Per Protocol’s applications,
              translating real-world research needs into practical, intuitive tools built
              around how research teams actually work.
            </p>
          </div>
        </article>

        <article className="team-member">
          <img
            src="/team/saejin-kim-heinert.png"
            width="1122"
            height="1402"
            loading="lazy"
            decoding="async"
            alt="Saejin Kim-Heinert"
          />
          <div className="team-copy">
            <h3>Saejin Kim-Heinert</h3>
            <span>Infrastructure Lead</span>
            <p>
              Saejin builds and maintains the infrastructure that makes Per Protocol’s tools
              available to the public. A software engineer with a particular interest in
              applied AI, he leads hosting and deployment and works with Maria to train and
              integrate specialized models for selected tools across the suite.
            </p>
          </div>
        </article>
      </div>
    </section>
  );
}

function AboutPage() {
  return (
    <div className="site-page about-page">
      <Header about />
      <main className="about">
        <span className="kicker">About</span>
        <h1>For the people<br />doing the research.</h1>
        <p className="about-lede">
          Per Protocol makes practical tools for the boots on the ground in clinical research—the
          teams doing the day-to-day work of running studies. We keep each tool focused on a real
          task and, whenever we can, link back to the original source so you can verify the
          information for yourself.
        </p>
        <div className="principles">
          <section>
            <span>01</span>
            <h2>Starts with the work</h2>
            <p>We build around specific tasks that come up in the day-to-day work of running a study.</p>
          </section>
          <section>
            <span>02</span>
            <h2>Check the source</h2>
            <p>Whenever possible, results link back to the original criteria, records, or public guidance so you can verify them yourself.</p>
          </section>
          <section>
            <span>03</span>
            <h2>Every minute matters</h2>
            <p>We know how valuable time is in a healthcare setting. We’re committed to a clear, seamless experience so our tools save time instead of taking more of it.</p>
          </section>
        </div>
        <nav className="about-links" aria-label="About page links">
          <a className="back-link" href="/">Explore the tools <span aria-hidden="true">→</span></a>
        </nav>
        <Team />
      </main>
    </div>
  );
}

export function App({ route }: { route: SiteRoute }) {
  return route === "about" ? <AboutPage /> : <LandingPage />;
}
